/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import { checkUrl } from '@umati/dashboard-common';
import SectionData from 'components/machine/sectionData';
import React, { ReactElement, useState } from 'react';
import { TemplateContext } from 'templates/pubSub/types/TemplateContextType';
import { SectionRowElementList } from 'templates/pubSub/types/TemplateType';
import { NodeIdentifier } from 'utilities/PubSub/PubSubAddressSpace';
import { usePubSubNode, usePubSubValue } from 'utilities/PubSub/PubSubHooks';
import TemplateHelperPubSub from 'utilities/TemplateHelperPubSub';

interface ISectionElementContentListProps {
  templateContext: TemplateContext;
  element: SectionRowElementList;
  cols: string;
  startingNodeId?: NodeIdentifier;
}

export default function SectionElementContentList(props: ISectionElementContentListProps): ReactElement | null {
  const valueFilters = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.element.valueVariable?.variablePath,
  );
  const valueNode = usePubSubNode(valueFilters, props.startingNodeId);
  const value = usePubSubValue(valueNode);

  const displayValues = TemplateHelperPubSub.getListDisplayValues(value, props.element.valueVariable);

  const labelFilters = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.element.labelVariable?.variablePath,
  );
  const labelNode = usePubSubNode(labelFilters, props.startingNodeId);
  const resolvedLabel = usePubSubValue(labelNode);

  const [expanded, setExpanded] = useState<boolean>(false);

  const label = resolvedLabel ?? props.element.label;
  const visibleItemCount = expanded ? displayValues.length : 3;
  const hiddenItemCount = displayValues.length - visibleItemCount;

  if (
    TemplateHelperPubSub.shouldHideOptionalVariable(
      value,
      props.element.valueVariable,
      `SectionElementContentList:${props.element.id}`,
    )
  ) {
    return null;
  }

  return (
    <SectionData cols={props.cols} label={label}>
      {displayValues.length < 1 && 'Undefined'}
      <ol className="value list-numbered">
        {displayValues.slice(0, visibleItemCount).map((displayValue, index) =>
          props.element.showAsLink && checkUrl(displayValue) ? (
            <li key={index}>
              <a href={displayValue}>{displayValue}</a>
            </li>
          ) : (
            <li key={index}>{displayValue}</li>
          ),
        )}
      </ol>
      {hiddenItemCount > 0 && (
        <span className="value">
          <a href="#" onClick={() => setExpanded(true)}>
            show more ({hiddenItemCount})
          </a>
        </span>
      )}
      {visibleItemCount > 3 && (
        <span className="value">
          <a href="#" onClick={() => setExpanded(false)}>
            show less
          </a>
        </span>
      )}
    </SectionData>
  );
}
